/**
 * Преобразование сериализованных таблиц редактора в WordprocessingML.
 *
 * Формат разметки и визуальное оформление сохраняют текущее поведение
 * Word-экспорта и покрыты characterization-тестами.
 */
const SERIALIZED_TABLE_TOKEN_REGEX = /([A-Za-z0-9_-]+\|colw=(?:\d+(?:\.\d+)?|\.\d+)(?:,(?:\d+(?:\.\d+)?|\.\d+))*\|(?:\d+_\d+!?_[^;\n\r]*)(?:;\d+_\d+!?_[^;\n\r]*)*)/g;

const normalizeWeight = (value, fallback = 1) => {
  const parsed = Number.parseFloat(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return fallback;
  return Math.min(8, Math.max(0.2, parsed));
};

const decodeCellValue = (value) =>
  String(value ?? '')
    .replace(/\\\\/g, '\u0000')
    .replace(/\\n/g, '\n')
    .replace(/\u0000/g, '\\');

export const splitLineByTableTokens = (line, tableMarkers) => {
  if (typeof line !== 'string' || !line) return [''];

  const segments = [];
  const regex = new RegExp(SERIALIZED_TABLE_TOKEN_REGEX.source, 'g');
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(line)) !== null) {
    const token = match[1];
    const parsedTable = parseSerializedTableToken(token);
    if (!parsedTable) {
      continue;
    }

    const before = line.slice(lastIndex, match.index);
    if (before) {
      segments.push(before);
    }

    const marker = `___SMART_TABLE_${tableMarkers.length}___`;
    tableMarkers.push({ marker, table: parsedTable });
    segments.push(marker);

    lastIndex = match.index + token.length;
  }

  const after = line.slice(lastIndex);
  if (after || segments.length === 0) {
    segments.push(after);
  }

  return segments;
};

const parseSerializedTableToken = (token) => {
  if (typeof token !== 'string' || !token.includes('|')) {
    return null;
  }

  const delimiterIndex = token.indexOf('|');
  if (delimiterIndex <= 0 || delimiterIndex === token.length - 1) {
    return null;
  }

  const tableId = token.slice(0, delimiterIndex).trim();
  let cellsRaw = token.slice(delimiterIndex + 1);

  if (!/^[A-Za-z0-9_-]+$/.test(tableId) || !cellsRaw) {
    return null;
  }

  const colwMatch = cellsRaw.match(/^colw=([0-9.,]+)\|/);
  if (!colwMatch) {
    return null;
  }
  const columnWeightsRaw = colwMatch[1]
    .split(',')
    .map((part) => normalizeWeight(part.trim(), 1))
    .filter((value) => Number.isFinite(value) && value > 0);
  if (columnWeightsRaw.length === 0) {
    return null;
  }
  cellsRaw = cellsRaw.slice(colwMatch[0].length);

  const entries = cellsRaw.split(';').filter(Boolean);
  if (entries.length === 0) {
    return null;
  }

  const cells = [];
  let maxRow = -1;
  let maxCol = -1;

  for (const entry of entries) {
    const cellMatch = entry.match(/^(\d+)_(\d+)(!)?_(.*)$/);
    if (!cellMatch) {
      return null;
    }
    const row = Number.parseInt(cellMatch[1], 10);
    const col = Number.parseInt(cellMatch[2], 10);
    const isBold = Boolean(cellMatch[3]);
    const value = decodeCellValue(cellMatch[4] ?? '');

    if (!Number.isFinite(row) || !Number.isFinite(col)) {
      return null;
    }

    if (row > maxRow) maxRow = row;
    if (col > maxCol) maxCol = col;
    cells.push({ row, col, value, isBold });
  }

  return {
    tableId,
    columnWeights: columnWeightsRaw,
    rows: maxRow + 1,
    cols: maxCol + 1,
    cells,
  };
};

const escapeXml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');

const buildWordRunsXml = (text, isBold = false) => {
  const safeText = String(text ?? '');
  if (!safeText) {
    return '';
  }

  const boldXml = isBold ? '<w:b/><w:bCs/>' : '';
  const runProps = `<w:rPr><w:rFonts w:ascii="Arial" w:hAnsi="Arial" w:cs="Arial"/>${boldXml}</w:rPr>`;
  const lines = safeText.split('\n');
  return lines
    .map((line, idx) => {
      const escaped = escapeXml(line);
      if (idx === 0) {
        return `<w:r>${runProps}<w:t xml:space="preserve">${escaped}</w:t></w:r>`;
      }
      return `<w:r>${runProps}<w:br/></w:r><w:r>${runProps}<w:t xml:space="preserve">${escaped}</w:t></w:r>`;
    })
    .join('');
};

const buildWordTableXml = (table) => {
  const rowsCount = Math.max(1, table.rows || 1);
  const colsCount = Math.max(1, table.cols || 1);
  const tableWidth = 10000;
  const defaultWeights = Array.from({ length: colsCount }, () => 1);
  const providedWeights = Array.isArray(table.columnWeights) ? table.columnWeights : [];
  const columnWeights = defaultWeights.map((_, idx) =>
    normalizeWeight(providedWeights[idx], 1)
  );
  const totalWeight = columnWeights.reduce((sum, value) => sum + value, 0) || colsCount;
  const colWidths = columnWeights.map((weight) => Math.max(300, Math.floor((tableWidth * weight) / totalWeight)));
  const widthSum = colWidths.reduce((sum, value) => sum + value, 0);
  const widthDelta = tableWidth - widthSum;
  if (colWidths.length > 0 && widthDelta !== 0) {
    colWidths[colWidths.length - 1] = Math.max(300, colWidths[colWidths.length - 1] + widthDelta);
  }

  const matrix = Array.from({ length: rowsCount }, () =>
    Array.from({ length: colsCount }, () => ({ value: '', isBold: false }))
  );

  table.cells.forEach(({ row, col, value, isBold }) => {
    if (row >= 0 && row < rowsCount && col >= 0 && col < colsCount) {
      matrix[row][col] = { value, isBold: Boolean(isBold) };
    }
  });

  const gridXml = colWidths.map((width) => `<w:gridCol w:w="${width}"/>`).join('');

  const rowsXml = matrix
    .map((row) => {
      const cellsXml = row
        .map((cellValue, colIndex) => {
          const runsXml = buildWordRunsXml(cellValue.value, cellValue.isBold);
          const cellWidth = colWidths[colIndex] || Math.max(300, Math.floor(tableWidth / colsCount));
          return `<w:tc><w:tcPr><w:tcW w:w="${cellWidth}" w:type="dxa"/><w:vAlign w:val="top"/></w:tcPr><w:p><w:pPr><w:jc w:val="left"/></w:pPr>${runsXml}</w:p></w:tc>`;
        })
        .join('');
      return `<w:tr>${cellsXml}</w:tr>`;
    })
    .join('');

  return `<w:tbl><w:tblPr><w:tblW w:w="${tableWidth}" w:type="dxa"/><w:jc w:val="left"/><w:tblLayout w:type="autofit"/><w:tblBorders><w:top w:val="double" w:sz="6" w:space="0" w:color="000000"/><w:left w:val="double" w:sz="6" w:space="0" w:color="000000"/><w:bottom w:val="double" w:sz="6" w:space="0" w:color="000000"/><w:right w:val="double" w:sz="6" w:space="0" w:color="000000"/><w:insideH w:val="double" w:sz="6" w:space="0" w:color="000000"/><w:insideV w:val="double" w:sz="6" w:space="0" w:color="000000"/></w:tblBorders></w:tblPr><w:tblGrid>${gridXml}</w:tblGrid>${rowsXml}</w:tbl>`;
};

export const replaceTableMarkersInWordXml = (documentXml, tableMarkers) => {
  if (!tableMarkers || tableMarkers.length === 0) {
    return documentXml;
  }

  const markerMap = new Map(tableMarkers.map((item) => [item.marker, item.table]));
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(documentXml, 'application/xml');
  const paragraphs = Array.from(xmlDoc.getElementsByTagName('w:p'));

  paragraphs.forEach((paragraph) => {
    const textNodes = Array.from(paragraph.getElementsByTagName('w:t'));
    if (textNodes.length === 0) return;

    const paragraphText = textNodes.map((node) => node.textContent || '').join('');
    const chunks = splitParagraphTextByMarkers(paragraphText, markerMap);
    const hasMarker = chunks.some((chunk) => chunk.type === 'marker');
    if (!hasMarker) return;

    const parent = paragraph.parentNode;
    if (!parent) return;

    chunks.forEach((chunk) => {
      if (chunk.type === 'text') {
        if (!chunk.value) return;
        const paragraphNode = createParagraphNode(xmlDoc, chunk.value);
        parent.insertBefore(paragraphNode, paragraph);
        return;
      }

      const table = markerMap.get(chunk.value);
      if (!table) return;
      const emptyBefore = createEmptyParagraphNode(xmlDoc);
      parent.insertBefore(emptyBefore, paragraph);
      const tableNode = createTableNode(xmlDoc, table);
      parent.insertBefore(tableNode, paragraph);
      const emptyAfter = createEmptyParagraphNode(xmlDoc);
      parent.insertBefore(emptyAfter, paragraph);
    });

    parent.removeChild(paragraph);
  });

  const serializer = new XMLSerializer();
  return serializer.serializeToString(xmlDoc);
};

const splitParagraphTextByMarkers = (text, markerMap) => {
  const markers = Array.from(markerMap.keys());
  const chunks = [];
  let cursor = 0;

  while (cursor < text.length) {
    let nearestIndex = -1;
    let nearestMarker = null;

    for (const marker of markers) {
      const idx = text.indexOf(marker, cursor);
      if (idx === -1) continue;
      if (nearestIndex === -1 || idx < nearestIndex) {
        nearestIndex = idx;
        nearestMarker = marker;
      }
    }

    if (nearestIndex === -1 || !nearestMarker) {
      chunks.push({ type: 'text', value: text.slice(cursor) });
      break;
    }

    const before = text.slice(cursor, nearestIndex);
    if (before) {
      chunks.push({ type: 'text', value: before });
    }

    chunks.push({ type: 'marker', value: nearestMarker });
    cursor = nearestIndex + nearestMarker.length;
  }

  if (chunks.length === 0) {
    chunks.push({ type: 'text', value: text });
  }

  return chunks;
};

const createParagraphNode = (xmlDoc, text) => {
  const runsXml = buildWordRunsXml(text);
  const paragraphXml = `<w:p xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main"><w:pPr><w:jc w:val="left"/></w:pPr>${runsXml}</w:p>`;
  const parsed = new DOMParser().parseFromString(paragraphXml, 'application/xml');
  return xmlDoc.importNode(parsed.documentElement, true);
};

const createEmptyParagraphNode = (xmlDoc) => {
  const paragraphXml = '<w:p xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main"><w:pPr><w:jc w:val="left"/></w:pPr></w:p>';
  const parsed = new DOMParser().parseFromString(paragraphXml, 'application/xml');
  return xmlDoc.importNode(parsed.documentElement, true);
};

const createTableNode = (xmlDoc, table) => {
  const tableXml = buildWordTableXml(table);
  const wrappedXml = `<root xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">${tableXml}</root>`;
  const parsed = new DOMParser().parseFromString(wrappedXml, 'application/xml');
  const tableElement = parsed.documentElement.firstElementChild;
  return xmlDoc.importNode(tableElement, true);
};
